import React, { useState, useCallback } from "react";
import { X, AlertTriangle, Camera, Info, Loader2 } from "lucide-react";
import { Site, IQWatchDeviceWithData, Alert, CameraSnapshotResponse } from "../../types";
import { getAlertsBySite } from "../../services/alertService.ts";
import { cameraService } from "../../services/cameraService.ts";

interface SiteDetailSlidePanelProps {
  site: Site;
  iqwatchDevices: IQWatchDeviceWithData[];
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  onDeviceClick?: (
    device: IQWatchDeviceWithData,
    tab?: "battery" | "router" | "cameras"
  ) => void;
}

type TabType = "info" | "alerts" | "cameras";

const SiteDetailSlidePanel: React.FC<SiteDetailSlidePanelProps> = ({
  site,
  iqwatchDevices,
  organizationId,
  isOpen,
  onClose,
  onDeviceClick,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("info");
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsLoaded, setAlertsLoaded] = useState(false);
  const [cameraSnapshots, setCameraSnapshots] = useState<
    Record<string, Record<string, CameraSnapshotResponse>>
  >({});
  const [deviceCameraOrder, setDeviceCameraOrder] = useState<
    Record<string, string[]>
  >({});
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [camerasLoaded, setCamerasLoaded] = useState(false);

  // Get all site IDs (including children)
  const getSiteIds = useCallback((s: Site): string[] => {
    const ids = [s.id];
    if (s.children) {
      s.children.forEach((child) => {
        ids.push(...getSiteIds(child));
      });
    }
    return ids;
  }, []);

  const siteIds = getSiteIds(site);
  const siteIqwatchDevices = iqwatchDevices.filter((device) =>
    siteIds.includes(device.device.site.id)
  );

  // Calculate device statistics
  const onlineCount = siteIqwatchDevices.filter(
    (device) => device.device.status === "running"
  ).length;
  const offlineCount = siteIqwatchDevices.filter(
    (device) =>
      device.device.status === "stopped" || device.device.status === "error"
  ).length;
  const lowBatteryCount = siteIqwatchDevices.filter((device) => {
    const soc = device.device.last_soc ? parseFloat(device.device.last_soc) : NaN;
    return !isNaN(soc) && soc < 50;
  }).length;
  const activeAlertsCount = alerts.filter((alert) => alert.status === "active").length;

  // Load alerts when Alerts tab is clicked
  const handleAlertsTabClick = useCallback(async () => {
    if (alertsLoaded || alertsLoading) {
      setActiveTab("alerts");
      return;
    }

    setActiveTab("alerts");
    setAlertsLoading(true);

    try {
      const response = await getAlertsBySite(organizationId, site.id);
      setAlerts(response.alerts);
      setAlertsLoaded(true);
    } catch (error) {
      console.error(`Failed to fetch alerts for site ${site.id}:`, error);
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [organizationId, site.id, alertsLoaded, alertsLoading]);

  // Load camera snapshots when Cameras tab is clicked
  const handleCamerasTabClick = useCallback(async () => {
    if (camerasLoaded || camerasLoading) {
      setActiveTab("cameras");
      return;
    }

    setActiveTab("cameras");
    setCamerasLoading(true);

    try {
      const snapshotPromises = siteIqwatchDevices.map(async (deviceWithData) => {
        const deviceId = deviceWithData.device.id;
        try {
          // Get cameras for device
          const camerasResponse = await cameraService.getCameras(deviceId);
          const cameras = camerasResponse.cameras || [];

          // Normalize camera order for consistent A/B/C... labeling
          const normalizeCameraOrder = (cameraIds: string[]) => {
            const parsed = cameraIds
              .map((id) => ({ id, num: parseInt(id, 10) }))
              .filter((x) => !isNaN(x.num));
            if (parsed.length === cameraIds.length) {
              return [...parsed].sort((a, b) => a.num - b.num).map((x) => x.id);
            }
            return cameraIds;
          };
          const cameraOrder = normalizeCameraOrder(cameras.map((c) => c.id));

          // Fetch snapshots for each camera
          const cameraSnapshotPromises = cameras.map(async (camera) => {
            try {
              const snapshotResponse = await cameraService.getCameraSnapshot(
                deviceId,
                camera.id
              );
              return { cameraId: camera.id, snapshot: snapshotResponse };
            } catch (error) {
              console.error(
                `Failed to fetch snapshot for camera ${camera.id}:`,
                error
              );
              return null;
            }
          });

          const snapshots = await Promise.allSettled(cameraSnapshotPromises);
          const deviceSnapshots: Record<string, CameraSnapshotResponse> = {};

          snapshots.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
              deviceSnapshots[result.value.cameraId] = result.value.snapshot;
            }
          });

          return { deviceId, snapshots: deviceSnapshots, cameraOrder };
        } catch (error) {
          console.error(`Failed to fetch cameras for device ${deviceId}:`, error);
          return { deviceId, snapshots: {}, cameraOrder: [] as string[] };
        }
      });

      const results = await Promise.allSettled(snapshotPromises);
      const allSnapshots: Record<string, Record<string, CameraSnapshotResponse>> = {};
      const allCameraOrders: Record<string, string[]> = {};

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          allSnapshots[result.value.deviceId] = result.value.snapshots;
          allCameraOrders[result.value.deviceId] = result.value.cameraOrder;
        }
      });

      setCameraSnapshots(allSnapshots);
      setDeviceCameraOrder(allCameraOrders);
      setCamerasLoaded(true);
    } catch (error) {
      console.error("Error fetching camera snapshots:", error);
    } finally {
      setCamerasLoading(false);
    }
  }, [siteIqwatchDevices, camerasLoaded, camerasLoading]);

  // Reset state when panel closes
  const handleClose = useCallback(() => {
    setActiveTab("info");
    setAlertsLoaded(false);
    setCamerasLoaded(false);
    setAlerts([]);
    setCameraSnapshots({});
    setDeviceCameraOrder({});
    onClose();
  }, [onClose]);

  // Get camera name helper (Device name + A/B/C... based on camera order per device)
  const getCameraName = (
    deviceName: string,
    deviceId: string,
    cameraId: string
  ): string => {
    const order = deviceCameraOrder[deviceId] || [];
    const idx = order.findIndex((id) => id === cameraId);
    if (idx >= 0 && idx < 26) {
      const suffix = String.fromCharCode(65 + idx); // A=65
      return `${deviceName} ${suffix}`;
    }
    return deviceName;
  };

  // Collect all camera snapshots
  const allCameraSnapshots: Array<{
    deviceId: string;
    deviceName: string;
    cameraId: string;
    cameraName: string;
    cloudId: string;
    snapshotUrl: string;
  }> = [];

  siteIqwatchDevices.forEach((device) => {
    const deviceSnapshots = cameraSnapshots[device.device.id] || {};
    Object.entries(deviceSnapshots).forEach(([cameraId, snapshotResponse]) => {
      if (snapshotResponse?.snapshot?.url) {
        const actualCameraId = snapshotResponse.camera_id || cameraId;
        allCameraSnapshots.push({
          deviceId: device.device.id,
          deviceName: device.device.name,
          cameraId: actualCameraId,
          cameraName: getCameraName(
            device.device.name,
            device.device.id,
            actualCameraId
          ),
          cloudId: snapshotResponse.snapshot.cloud_id || actualCameraId,
          snapshotUrl: snapshotResponse.snapshot.url,
        });
      }
    });
  });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Slide-in Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{site.name}</h2>
            {site.detail?.address && (
              <p className="text-sm text-gray-500 mt-1">{site.detail.address}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white">
          <button
            onClick={() => setActiveTab("info")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "info"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Info className="w-4 h-4" />
              <span>Info</span>
            </div>
          </button>
          <button
            onClick={handleAlertsTabClick}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === "alerts"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Alerts</span>
              {activeAlertsCount > 0 && (
                <span className="absolute top-1 right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeAlertsCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={handleCamerasTabClick}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "cameras"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Camera className="w-4 h-4" />
              <span>Cameras</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {/* Info Tab */}
          {activeTab === "info" && (
            <div className="space-y-4">
              {/* Site Statistics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Total Devices</div>
                  <div className="text-2xl font-semibold text-blue-600">
                    {siteIqwatchDevices.length}
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Online</div>
                  <div className="text-2xl font-semibold text-green-600">
                    {onlineCount}
                  </div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Offline</div>
                  <div className="text-2xl font-semibold text-red-600">
                    {offlineCount}
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Low Battery</div>
                  <div className="text-2xl font-semibold text-yellow-600">
                    {lowBatteryCount}
                  </div>
                </div>
              </div>

              {/* Device List */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Devices ({siteIqwatchDevices.length})
                </h3>
                <div className="space-y-2">
                  {siteIqwatchDevices.map((device) => {
                    const soc = device.device.last_soc
                      ? parseFloat(device.device.last_soc)
                      : NaN;
                    const batteryLevel = !isNaN(soc) ? soc : 0;
                    const voltage = device.device.last_battery_voltage
                      ? parseFloat(device.device.last_battery_voltage)
                      : NaN;
                    const batteryVoltage = !isNaN(voltage) ? voltage : 0;

                    const statusColor =
                      device.device.status === "running"
                        ? "green"
                        : device.device.status === "stopped"
                        ? "red"
                        : "orange";
                    const statusText =
                      device.device.status === "running"
                        ? "Online"
                        : device.device.status === "stopped"
                        ? "Offline"
                        : "Error";

                    const batteryColorClass =
                      batteryLevel < 20
                        ? "text-red-600"
                        : batteryLevel < 50
                        ? "text-yellow-600"
                        : "text-green-600";

                    return (
                      <div
                        key={device.device.id}
                        className={`border-l-4 border-${statusColor}-400 bg-gray-50 rounded-r p-3 cursor-pointer hover:bg-gray-100 transition-colors`}
                        onClick={() => onDeviceClick?.(device)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium text-gray-800">
                            {device.device.name}
                          </div>
                          <div className="flex items-center">
                            <span
                              className={`inline-block w-2 h-2 bg-${statusColor}-500 rounded-full mr-1`}
                            />
                            <span className={`text-xs text-${statusColor}-600 font-medium`}>
                              {statusText}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center text-xs">
                          <div className="flex items-center flex-1">
                            <span className="mr-1">🔋</span>
                            <span className={`${batteryColorClass} font-medium`}>
                              {batteryLevel.toFixed(1)}%
                            </span>
                            {batteryVoltage > 0 && (
                              <span className="ml-1 text-gray-500">
                                ({batteryVoltage.toFixed(2)}V)
                              </span>
                            )}
                          </div>
                          <div className="text-gray-500 text-xs ml-4">
                            ID: {device.device.iqwatch_device_id}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === "alerts" && (
            <div className="space-y-4">
              {alertsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading alerts...</span>
                </div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No alerts found for this site</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Active Alerts ({activeAlertsCount})
                    </h3>
                    <a
                      href={`/alerts?siteId=${site.id}`}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      View All Alerts
                    </a>
                  </div>
                  <div className="space-y-2">
                    {alerts
                      .filter((alert) => alert.status === "active")
                      .map((alert) => (
                        <div
                          key={alert.id}
                          className="bg-red-50 border border-red-200 rounded-lg p-3"
                        >
                          <div className="text-sm font-medium text-red-800">
                            {alert.rule_name}
                          </div>
                          <div className="text-xs text-red-600 mt-1">
                            {alert.device_name}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {alert.triggered_value} (threshold: {alert.threshold_value})
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Cameras Tab */}
          {activeTab === "cameras" && (
            <div className="space-y-4">
              {camerasLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">
                    Loading camera snapshots...
                  </span>
                </div>
              ) : allCameraSnapshots.length === 0 ? (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    {camerasLoaded
                      ? "No camera snapshots available"
                      : "Click to load camera snapshots"}
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Camera Snapshots ({allCameraSnapshots.length})
                  </h3>
                  <div className="space-y-4">
                    {allCameraSnapshots.map((cam) => (
                      <div
                        key={`${cam.deviceId}-${cam.cameraId}`}
                        className="flex flex-col cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          const device = siteIqwatchDevices.find(
                            (d) => d.device.id === cam.deviceId
                          );
                          if (device) {
                            onDeviceClick?.(device, "cameras");
                          }
                        }}
                      >
                        <div className="relative w-full rounded-lg border border-gray-200 bg-gray-50 aspect-video flex items-center justify-center overflow-hidden shadow-sm">
                          <img
                            src={cam.snapshotUrl}
                            alt={cam.cloudId}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.parentElement!.innerHTML =
                                '<div class="text-xs text-gray-400 text-center p-2">Snapshot<br/>not available</div>';
                            }}
                          />
                        </div>
                        <div className="mt-2 text-sm font-medium text-gray-700 text-center">
                          {cam.cameraName}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SiteDetailSlidePanel;
